/**
 * Dispatches List Page Component
 * /admin/dispatches
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import apiClient from '../api/client';
import ExportMenu from '../components/ExportMenu';

const Dispatches = () => {
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadDispatches();
  }, []);

  const loadDispatches = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiClient.getDispatches({ search: searchTerm });
      // Handle paginated response
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      setDispatches(data);
    } catch (err) {
      setError(err.message || 'Failed to load dispatches');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    loadDispatches();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  if (loading && dispatches.length === 0) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Dispatches
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View all vehicle dispatch records
          </Typography>
        </Box>
        <ExportMenu
          resource="bookings/dispatch"
          params={{ search: searchTerm }}
          onError={setError}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box mb={2}>
        <form onSubmit={handleSearchSubmit}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search dispatches..."
            value={searchTerm}
            onChange={handleSearch}
            slotProps={{ input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            } }}
          />
        </form>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Booking</TableCell>
              <TableCell>Vehicle</TableCell>
              <TableCell>Dispatched At</TableCell>
              <TableCell>Start Odometer</TableCell>
              <TableCell>End Odometer</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dispatches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No dispatches found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              dispatches.map((dispatch) => (
                <TableRow key={dispatch.id} hover>
                  <TableCell>
                    {dispatch.booking_detail?.booking_reference || dispatch.booking || '-'}
                  </TableCell>
                  <TableCell>
                    {dispatch.vehicle_detail?.plate_number || dispatch.vehicle || '-'}
                  </TableCell>
                  <TableCell>{formatDate(dispatch.dispatched_at)}</TableCell>
                  <TableCell>
                    {dispatch.estimated_start_odometer ? `${dispatch.estimated_start_odometer.toLocaleString()} km` : '-'}
                  </TableCell>
                  <TableCell>
                    {dispatch.estimated_end_odometer ? `${dispatch.estimated_end_odometer.toLocaleString()} km` : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/admin/dispatches/${dispatch.id}`)}
                      title="View Details"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default Dispatches;

